import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
    Alert, AlertIcon, Box, Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter,
    ModalHeader, ModalOverlay, Stack, Text, Textarea,
} from "@chakra-ui/react";
import { markRefunded } from "./paymentsApi";
import PaymentField from "./PaymentField";

/**
 * Builds the initial form state from a payment object.
 * @param {Object|null} payment - Payment object
 * @returns {Object}
 */
function buildInitialForm(payment) {
    const fallback = payment?.total_amount ?? "";
    return { refunded_amount: String(fallback ?? ""), note: "" };
}

/**
 * Validates the form before submission.
 * @param {Object} form - Form state
 * @returns {string|null} Error or null
 */
function validateForm(form) {
    if (form.refunded_amount === "" || form.refunded_amount === null) return null;
    const amount = Number(form.refunded_amount);
    if (Number.isNaN(amount) || amount < 0) return "Le montant doit être un nombre positif";
    return null;
}

/**
 * Builds the API payload from form state.
 * @param {Object} form - Form state
 * @returns {Object}
 */
function buildPayload(form) {
    const payload = {};
    if (form.refunded_amount !== "" && form.refunded_amount !== null) payload.refunded_amount = Number(form.refunded_amount);
    if (form.note?.trim()) payload.note = form.note.trim();
    return payload;
}

/**
 * Submits the payload and surfaces toasts.
 * @param {Object} args - { paymentId, form, setLoading, onMarked, onClose }
 */
async function submit({ paymentId, form, setLoading, onMarked, onClose }) {
    const err = validateForm(form);
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
        await markRefunded(paymentId, buildPayload(form));
        toast.success("Paiement marqué comme remboursé");
        onMarked?.();
        onClose();
    } catch (error) {
        const msg = error?.response?.data || "Échec de l'action";
        toast.error(typeof msg === "string" ? msg : "Échec de l'action");
    } finally {
        setLoading(false);
    }
}

/**
 * Renders a warning banner explaining the action's irreversibility.
 * @returns {React.ReactElement}
 */
function WarningBanner() {
    return (
        <Alert status="error" borderRadius="12px" alignItems="flex-start">
            <AlertIcon />
            <Box>
                <Text fontSize="0.78rem" fontWeight="600">Action irréversible</Text>
                <Text fontSize="0.72rem" mt="2px">
                    Utilise cette option uniquement si le remboursement a été traité hors plateforme (virement SEPA manuel ou transfert Orange Money).
                </Text>
            </Box>
        </Alert>
    );
}

/**
 * Dialog confirming a manual refund (off-Stripe SEPA case).
 * @param {Object} props - { open, payment, onClose, onMarked }
 * @returns {React.ReactElement}
 */
function MarkRefundedDialog({ open, payment, onClose, onMarked }) {
    const [form, setForm] = useState(buildInitialForm(payment));
    const [loading, setLoading] = useState(false);
    useEffect(() => { if (open) setForm(buildInitialForm(payment)); }, [open, payment]);
    const onChange = (key, value) => setForm((f) => ({ ...f, [key]: value }));
    const onSubmit = () => submit({ paymentId: payment?.id, form, setLoading, onMarked, onClose });
    return (
        <Modal isOpen={open} onClose={onClose} size="lg" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Marquer comme remboursé</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Stack spacing="16px">
                        <WarningBanner />
                        <PaymentField label="Montant remboursé" type="number" value={form.refunded_amount}
                            onChange={(v) => onChange("refunded_amount", v)}
                            helper={`Laisser vide pour rembourser le total (${payment?.total_amount ?? "—"})`} />
                        <PaymentField label="Note interne (optionnel)" helper="Stocké dans l'audit trail">
                            <Textarea value={form.note} onChange={(e) => onChange("note", e.target.value)} rows={2} borderRadius="12px" />
                        </PaymentField>
                    </Stack>
                </ModalBody>
                <ModalFooter gap="8px">
                    <Button variant="ghost" onClick={onClose}>Annuler</Button>
                    <Button colorScheme="red" onClick={onSubmit} isLoading={loading} isDisabled={!payment?.id}
                        loadingText="Action en cours...">Confirmer le remboursement</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default MarkRefundedDialog;
